/**
 * A tenant's settings, all in one place — each with its resolved value, where
 * that value comes from, and an editor.
 *
 * A setting resolves tenant → the "platform" tenant's defaults → the code
 * default, and is overridden WHOLE: there is no merging inside a setting. So
 * each section says which layer supplied its value, and "Use the platform
 * default" removes the tenant's value outright. On the "platform" tenant this
 * tab edits those defaults and leaves out settings that only mean something
 * inside one tenant.
 *
 * Adding a setting: an entry in SECTIONS and its editor. The endpoint already
 * returns every registered setting. See
 * docs/FeatureDesigns/multi-tenancy/tenant-settings-target.md §5.
 */
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, CardContent, CardHeader } from '@shared-ui/components';
import {
    tenantsApi, type TenantSettingKey, type TenantSettingSource, type TenantSettingValues,
} from '../../../api/tenantsApi';
import { AnswerSheetsEditor } from './AnswerSheetsSetting';
import { OmrDefaultEditor } from './OmrDefaultSetting';
import { WorkingEditor } from './WorkingSetting';
import { DiagnosisReviewEditor } from './DiagnosisReviewSetting';
import { SolutionRegionDisplayEditor } from './SolutionRegionDisplaySetting';
import { CorrectAnswersEditor } from './CorrectAnswersSetting';
import { ResultReleaseEditor } from './ResultReleaseSetting';
import { OmrDesignsCard } from './OmrDesignsCard';
import { PLATFORM_TENANT_ID } from './tenantHelpers';

/** What every setting's editor receives. The editor keeps its own draft and
 *  calls `save` with the whole new value, or null to stop setting it here. */
export interface SettingEditorProps<K extends TenantSettingKey> {
    tenantId: string;
    value: NonNullable<TenantSettingValues[K]>;
    save: (value: TenantSettingValues[K] | null) => void;
    saving: boolean;
    /** The section's own buttons ("Use the platform default"), placed in the
     *  editor's save row so a section has one row of actions. */
    actions?: ReactNode;
}

interface Section<K extends TenantSettingKey> {
    key: K;
    title: string;
    subtitle: string;
    /** Mirrors the backend registry: may the platform set a default? */
    platformDefault: boolean;
    Editor: (props: SettingEditorProps<K>) => ReactNode;
    /** Shown under the section, for context the setting points into. */
    below?: (tenantId: string) => ReactNode;
}

const SECTIONS: Array<Section<'answerSheets'> | Section<'omr'> | Section<'working'> | Section<'diagnosisReview'> | Section<'solutionRegionDisplay'> | Section<'correctAnswers'> | Section<'resultRelease'>> = [
    {
        key: 'answerSheets',
        title: 'Answer sheets',
        subtitle: 'How students give their answers, for assignments and for tests. A paper can override this at import.',
        platformDefault: true,
        Editor: AnswerSheetsEditor,
    },
    {
        key: 'omr',
        title: 'OMR default sheet',
        subtitle: 'The design a paper starts from at import, and reads a sheet with when its paper names none.',
        platformDefault: false,
        Editor: OmrDefaultEditor,
        below: tenantId => <OmrDesignsCard tenantId={tenantId} />,
    },
    {
        key: 'working',
        title: 'Working',
        subtitle: 'Whether the working is marked, or rough work and only the answer counts. Decides what feedback on a right answer is worth giving. A paper or an assignment can say otherwise.',
        platformDefault: true,
        Editor: WorkingEditor,
    },
    {
        key: 'diagnosisReview',
        title: 'Diagnosis review',
        subtitle: 'Diagnoses the system is unsure of are put to the teacher to approve or reject, and are not shown to students or counted until then. Medium-confidence ones always are; this decides whether low-confidence ones are too, or are withheld.',
        platformDefault: true,
        Editor: DiagnosisReviewEditor,
    },
    {
        key: 'solutionRegionDisplay',
        title: 'Solution region on uploaded pages',
        subtitle: 'When the region hint is on, an upload draws a box around each question’s work on the pages the model reads. These boxes are often in the wrong place. This decides whether teachers and students see that box, or the pages as uploaded. Attempts without a box are unaffected; admin always sees what the model read.',
        platformDefault: true,
        Editor: SolutionRegionDisplayEditor,
    },
    {
        key: 'correctAnswers',
        title: 'Correct answers on the answer list',
        subtitle: 'When a student hands in an answer list (OMR or written) with the working, a question the list shows correct can be marked against the key without analysing its working. Wrong answers are always analysed. Only for MCQ, multi-correct and numerical questions, and only for answers on a page marked as the answer list or tapped in the app.',
        platformDefault: true,
        Editor: CorrectAnswersEditor,
    },
    {
        key: 'resultRelease',
        title: 'Results',
        subtitle: 'Whether students see a new assignment\'s or test\'s results as soon as it is marked, or only when the teacher releases them. This is what the form starts at when a paper is created; the teacher can change it on the paper and when assigning.',
        platformDefault: true,
        Editor: ResultReleaseEditor,
    },
];

const SOURCE_BADGE: Record<TenantSettingSource, { label: string; variant: 'success' | 'info' | 'default' }> = {
    tenant: { label: 'Set here', variant: 'success' },
    platform: { label: 'Platform default', variant: 'info' },
    default: { label: 'Code default', variant: 'default' },
};

export function TenantSettingsTab({ tenantId }: { tenantId: string }) {
    const queryClient = useQueryClient();
    const isPlatform = tenantId === PLATFORM_TENANT_ID;

    const { data, isLoading, error } = useQuery({
        queryKey: ['tenant-settings', tenantId],
        queryFn: () => tenantsApi.settings(tenantId),
    });

    const save = useMutation({
        mutationFn: ({ key, value }: { key: TenantSettingKey; value: unknown }) =>
            tenantsApi.updateSettings(tenantId, { settings: { [key]: value } }),
        onSuccess: next => {
            queryClient.setQueryData(['tenant-settings', tenantId], next);
            // The designs card marks the default.
            queryClient.invalidateQueries({ queryKey: ['tenant-omr-sheets', tenantId] });
        },
    });

    if (isLoading) return <div className="loading" style={{ padding: 16 }}>Loading settings…</div>;
    if (error || !data) return <div className="tenants-error">{(error as Error | null)?.message ?? 'Settings could not be loaded.'}</div>;

    const sections = isPlatform ? SECTIONS.filter(s => s.platformDefault) : SECTIONS;

    return (
        <>
            <div className="tenants-info">
                {isPlatform
                    ? 'Platform defaults — what every tenant that sets nothing of its own gets. A tenant replaces a setting whole; nothing is merged.'
                    : 'Each setting is this tenant’s own value, else the platform default, else the code default. A tenant’s value replaces the default whole.'}
            </div>
            {sections.map(section => {
                const resolved = data.settings[section.key];
                const badge = SOURCE_BADGE[resolved.source];
                const pending = save.isPending && save.variables?.key === section.key;
                const failed = save.isError && save.variables?.key === section.key;
                const setHere = isPlatform ? resolved.source === 'platform' : resolved.source === 'tenant';
                const clearLabel = isPlatform || !section.platformDefault ? 'Use the code default' : 'Use the platform default';
                const Editor = section.Editor as (props: SettingEditorProps<TenantSettingKey>) => ReactNode;
                return (
                    <div key={section.key}>
                        <Card padding="none">
                            <CardHeader
                                title={section.title}
                                subtitle={section.subtitle}
                                action={<Badge variant={badge.variant} title="Where this value comes from">{badge.label}</Badge>}
                            />
                            <CardContent>
                                {failed && <div className="tenants-error">{(save.error as Error).message}</div>}
                                <Editor
                                    // Remount on a new saved value, so the draft starts from it.
                                    key={JSON.stringify(resolved.value)}
                                    tenantId={tenantId}
                                    value={resolved.value}
                                    saving={pending}
                                    save={value => save.mutate({ key: section.key, value })}
                                    actions={setHere && (
                                        <Button
                                            variant="secondary"
                                            onClick={() => save.mutate({ key: section.key, value: null })}
                                            disabled={save.isPending}
                                        >
                                            {clearLabel}
                                        </Button>
                                    )}
                                />
                            </CardContent>
                        </Card>
                        {section.below?.(tenantId)}
                    </div>
                );
            })}
        </>
    );
}
